import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../lib/api";

/** Refresh this long before the signed URL actually expires. */
const SAFETY_MS = 30_000;
/** Automatic refreshes allowed per failure sequence. */
const MAX_AUTO_REFRESH = 2;
/** Uninterrupted playback that clears the failure sequence. */
const HEALTHY_MS = 30_000;

/** A fetched URL plus a counter, which keys the element so every fetch loads. */
interface Source {
  url: string;
  n: number;
}

interface Failure {
  message: string;
  /** No retry can help: the sign-in is gone, or the sound is not there. */
  final: boolean;
}

/**
 * Plays one library item through a short-lived, owner-only URL fetched on
 * demand.
 *
 * The URL is a credential. It is written to the <audio> element's src and
 * nowhere else: not to the console, not to storage, not into any message
 * shown to the user. When it expires or the element reports a media error,
 * the player fetches a fresh one, puts playback back where it was and
 * carries on. Refreshes are bounded so a permanently broken source turns
 * into a visible error instead of a loop.
 */
export function Player({ itemId, loop, autoLoad = true }: { itemId: string; loop: boolean; autoLoad?: boolean }) {
  const [source, setSource] = useState<Source | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [loading, setLoading] = useState(false);

  const audio = useRef<HTMLAudioElement | null>(null);
  /** When the current URL stops being usable, safety margin already applied. */
  const usableUntil = useRef(0);
  /** Automatic refreshes in the current failure sequence. */
  const autoRefreshes = useRef(0);
  /** Where to put playback once the new source has loaded. */
  const resumeAt = useRef<{ time: number; play: boolean }>({ time: 0, play: false });
  const healthyTimer = useRef<number | null>(null);
  const inFlight = useRef(false);
  const alive = useRef(true);

  const clearHealthyTimer = () => {
    if (healthyTimer.current !== null) window.clearTimeout(healthyTimer.current);
    healthyTimer.current = null;
  };

  const fetchUrl = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    const el = audio.current;
    // Keep the position only for this same item; the component resets when
    // itemId changes, so there is never another item's position to restore.
    resumeAt.current = el
      ? { time: Number.isFinite(el.currentTime) ? el.currentTime : 0, play: !el.paused && !el.ended }
      : { time: 0, play: false };
    setLoading(true);
    try {
      const answer = await api.audioUrl(itemId);
      if (!alive.current) return;
      // A lifetime inside the safety margin leaves no usable window; mark
      // it 0 so play never refreshes proactively (every press would refetch
      // and remount the element). The bounded media-failure path still
      // covers a URL that actually dies.
      const usable = answer.expires_in * 1000 - SAFETY_MS;
      usableUntil.current = usable > 0 ? Date.now() + usable : 0;
      setFailure(null);
      setSource((prev) => ({ url: answer.url, n: (prev?.n ?? 0) + 1 }));
    } catch (error: unknown) {
      if (!alive.current) return;
      // Never include the URL or the raw response in what the user is shown.
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        setFailure({ message: "Your sign-in expired. Sign in again to play this sound.", final: true });
      } else if (error instanceof ApiError && error.status === 404) {
        setFailure({ message: "This sound is no longer available.", final: true });
      } else {
        setFailure({ message: "SoundLayer could not prepare this preview.", final: false });
      }
    } finally {
      inFlight.current = false;
      if (alive.current) setLoading(false);
    }
  }, [itemId]);

  // One player per item. A new item starts from nothing: no inherited
  // position, no inherited failure budget.
  useEffect(() => {
    alive.current = true;
    setSource(null);
    setFailure(null);
    setLoading(false);
    usableUntil.current = 0;
    autoRefreshes.current = 0;
    resumeAt.current = { time: 0, play: false };
    clearHealthyTimer();
    if (autoLoad) void fetchUrl();
    return () => {
      alive.current = false;
      clearHealthyTimer();
    };
  }, [itemId, autoLoad, fetchUrl]);

  /** A media failure: refresh while the budget allows, then say so. */
  const onMediaFailure = () => {
    clearHealthyTimer();
    if (failure?.final) return;
    if (autoRefreshes.current >= MAX_AUTO_REFRESH) {
      setFailure({ message: "This preview stopped playing and could not be restarted.", final: false });
      return;
    }
    autoRefreshes.current += 1;
    void fetchUrl();
  };

  const onPlay = () => {
    // 0 = no usable window was ever granted; otherwise refresh only once the
    // margin has passed, swapping in a fresh URL before the element fails.
    if (usableUntil.current === 0 || Date.now() < usableUntil.current) return;
    void fetchUrl();
  };

  const onPlaying = () => {
    clearHealthyTimer();
    healthyTimer.current = window.setTimeout(() => {
      autoRefreshes.current = 0;
    }, HEALTHY_MS);
  };

  const onLoadedMetadata = () => {
    const el = audio.current;
    if (!el) return;
    const { time, play } = resumeAt.current;
    resumeAt.current = { time: 0, play: false };
    if (time > 0 && Number.isFinite(el.duration) && time < el.duration) {
      try {
        el.currentTime = time;
      } catch {
        /* the element refused the seek; playing from the start is better than nothing */
      }
    }
    if (play) void el.play().catch(() => undefined);
  };

  /** One explicit fetch. It does not restore the automatic budget; only 30 s
   *  of real playback does that. */
  const retry = () => {
    void fetchUrl();
  };

  return (
    <div className="player-shell">
      {source && (
        <audio
          // A new source gets a new element: one load, one error at most, and
          // no stale buffered range from the URL that just failed.
          key={source.n}
          ref={audio}
          className="player"
          controls
          preload="metadata"
          src={source.url}
          loop={loop}
          aria-label="Sound preview"
          onLoadedMetadata={onLoadedMetadata}
          onPlay={onPlay}
          onPlaying={onPlaying}
          onError={onMediaFailure}
          onStalled={() => {
            // A stall on a URL that is still good is buffering, not expiry.
            if (Date.now() >= usableUntil.current) onMediaFailure();
          }}
          onPause={clearHealthyTimer}
        >
          Your browser cannot play this audio.
        </audio>
      )}
      {!source && !failure && <p className="small muted player-note">{loading ? "Preparing preview…" : ""}</p>}
      {failure && (
        <div className="player-note stack" style={{ gap: 8 }}>
          <p className="small muted" style={{ margin: 0 }} role="alert">
            {failure.message}
          </p>
          {!failure.final && (
            <div className="row">
              <button className="btn" type="button" disabled={loading} onClick={retry}>
                Try again
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
